import React from 'react'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'
import Circle from '~/components/Circle'
import Text, { FontStyle } from '~/components/core/Text'
import FillSlider from '~/components/FillSlider'
import { CallTranscript } from '~/types/CallTranscript'

interface AgentTalkSpeedStatWrapperProps {
  $width: number
  $margin?: string
}

const HoldTimeAndPausesStatWrapper = styled.div<AgentTalkSpeedStatWrapperProps>`
  display: flex;
  flex-direction: column;
  width: ${(props) => props.$width}px;
  margin: ${(props) => props.$margin ?? ''};
`

const StatsWrapper = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  height: 15px;
  margin-bottom: 1px;
`

interface AgentTalkSpeedStatProps {
  transcript: CallTranscript
  width?: number
  margin?: string
}

const AgentTalkSpeedStat = ({
  transcript,
  width,
  margin,
}: AgentTalkSpeedStatProps) => {
  const { t } = useTranslation()
  //based on google... 170 wpm is average-high.  Let's pad this to 225 words per minute as the total amount of time
  const maxWordsPerMinute = 250
  const agentWordsPerMinute =
    transcript.ConversationCharacteristics.TalkSpeed.DetailsByParticipant.AGENT
      ?.AverageWordsPerMinute ?? 0

  const percentage = Math.min(agentWordsPerMinute / maxWordsPerMinute, 1)

  const sadColor = 'var(--chakra-colors-red-solid)'
  const neutralColor = 'var(--chakra-colors-bg-muted)'

  const tooHigh = 160
  const tooLow = 110

  //prettier-ignore
  const fillColor =
    agentWordsPerMinute >= tooHigh || agentWordsPerMinute <= tooLow
      ? sadColor
      : neutralColor

  return (
    <HoldTimeAndPausesStatWrapper $width={width ?? 300} $margin={margin}>
      <Text fontStyle={FontStyle.ButtonBold}>
        {t('callAnalytics.stats.agentTalkSpeed', 'Agent Talk Speed')}
      </Text>
      <FillSlider
        height={14}
        width={width ?? 300}
        fillPercentage={percentage}
        fillColor={fillColor}
        fillOutline={`1px solid var(--chakra-colors-bg-subtle)`}
        backgroundColor="var(--chakra-colors-bg-muted)"
        outline={`1px solid var(--chakra-colors-border-default)`}
        borderRadius={4}
        margin="4px 0px 3px 0px"
        animate
      />
      <StatsWrapper>
        <Circle radius={6} color={fillColor} margin="0px 5px 0px 0px" />
        <Text fontStyle={FontStyle.TinySmall}>
          {t(
            'callAnalytics.stats.wordsPerMinute',
            '{{count}} Words Per Minute (averaged)',
            { count: agentWordsPerMinute },
          )}
        </Text>
      </StatsWrapper>
    </HoldTimeAndPausesStatWrapper>
  )
}

export default AgentTalkSpeedStat
